import React from 'react';

const colors = { orange: '#fa741b', line: '#d9dadd', gray: '#f3f3f4', ink: '#34383d' };

// Simplified product illustrations based on the supplied PG-Solution screens.
// The neighboring ordered workflow provides the accessible explanation.
export default function PGSolutionWorkflowIllustration({ stage }) {
  return (
    <svg className="pg-workflow-illustration" viewBox="0 0 560 380" fill="none" aria-hidden="true" focusable="false">
      <g fontFamily="Arial, sans-serif" fontSize="12" fill={colors.ink}>
        {stage === 'prepare' && <PrepareScreen />}
        {stage === 'simulate' && <SimulationScreen />}
        {stage === 'manage' && <ManagementScreen />}
      </g>
    </svg>
  );
}

function Panel({ x, y, width, height, title, orange = false, children }) {
  return <g transform={`translate(${x} ${y})`}>
    <rect width={width} height={height} rx="7" fill="white" stroke={colors.line} />
    <path d={`M7 0H${width - 7}Q${width} 0 ${width} 7V26H0V7Q0 0 7 0Z`} fill={orange ? colors.orange : colors.gray} />
    <circle cx="12" cy="13" r="4" stroke={orange ? 'white' : colors.orange} strokeWidth="1.5" fill="none" />
    <text x="23" y="17" fill={orange ? 'white' : colors.ink} fontWeight="700">{title}</text>
    {children}
  </g>;
}

function CodePane({ x, y, sub = false, highlight = false }) {
  const lines = sub
    ? ['O1421 (SUB)', 'G40 G18 G0', 'G310 Z11.0', 'T0', 'M500', 'T222 M3300', 'G50 W-125.84', 'G99 G0 X-12.5', 'G1 Z-9.8 F0.07']
    : ['O1420 (MAIN)', 'T0100', 'M595', 'T0101 M3300', 'G50 W-1.7', 'G99 G0 X17.0', 'G1 X16.5 F0.5', 'M92', 'M30'];
  return <g transform={`translate(${x} ${y})`}>
    <rect width="166" height="19" fill="#fff1e6" />
    <text x="8" y="13" fontSize="10">{sub ? 'O1421_H2' : 'O1420_H1'}</text>
    <path d="M0 19H166" stroke={colors.orange} fill="none" />
    {lines.map((line, index) => <g key={line} transform={`translate(0 ${34 + index * 13})`}>
      {highlight && index === 7 && <rect x="20" y="-10" width="137" height="13" fill="#ffe0c9" />}
      <text x="4" fill="#65a6b2" fontFamily="monospace" fontSize="9">{index + 1}</text>
      <text x="22" fontFamily="monospace" fontSize="10" fill={index === 0 ? '#479449' : index % 3 === 0 ? '#b340bc' : '#30599c'}>{line}</text>
    </g>)}
    <rect x="163" y="25" width="3" height="123" fill="#e6e6e6" />
  </g>;
}

function PrepareScreen() {
  return <>
    <Panel x={8} y={8} width={544} height={242} title="PROGRAM" orange>
      <rect x="8" y="34" width="176" height="198" rx="4" fill="#f4f4f4" stroke={colors.line} />
      <text x="18" y="53" fontWeight="700" fill="#b94b08">G/M Guide</text>
      <text x="18" y="75" fontWeight="700">G50</text>
      <text x="18" y="91" fontSize="10">Coordinate setting</text>
      <rect x="16" y="101" width="160" height="19" fill="#818181" />
      <text x="23" y="114" fill="white" fontSize="10">Name</text><text x="75" y="114" fill="white" fontSize="10">Description</text>
      {['X Position', 'Y Position', 'Z Position'].map((label, index) => <g key={label}>
        <text x="29" y={136 + index * 20} fontSize="10">{label[0]}</text>
        <text x="75" y={136 + index * 20} fontSize="10">{label}</text>
        <path d={`M16 ${142 + index * 20}H176`} stroke={colors.line} fill="none" />
      </g>)}
      <rect x="16" y="188" width="160" height="32" rx="4" fill="#fff1e6" stroke="#f8ba8e" />
      <path d="M27 197H37V212H27ZM30 201H34M30 205H34" stroke={colors.orange} fill="none" />
      <text x="46" y="208" fontSize="11" fontWeight="700" fill="#ac490e">Templates</text>
      <CodePane x={194} y={34} highlight /><CodePane x={370} y={34} sub />
      <rect x="194" y="190" width="342" height="17" fill={colors.orange} />
      <text x="201" y="202" fill="white" fontSize="10" fontWeight="700">LOG / Error</text>
      <rect x="194" y="207" width="342" height="24" fill="#ffdbc0" />
      <text x="201" y="223" fontSize="10">M92</text><text x="238" y="223" fontSize="10">Not exist Program</text>
      <text x="485" y="223" fontSize="10">Line 8</text>
    </Panel>
    <path d="M280 253V269m-5-5 5 5 5-5" stroke={colors.orange} strokeWidth="2" fill="none" />
    <Panel x={8} y={276} width={544} height={96} title="C/T calculation">
      <CycleBar y={38} label="Path1" widths={[22, 39, 115, 22, 42, 58, 27, 68, 38]} />
      <CycleBar y={65} label="Path2" widths={[9, 52, 45, 60, 34, 195, 36]} />
    </Panel>
  </>;
}

function CycleBar({ y, label, widths }) {
  const palette = ['#4aa6bd', '#8965a1', '#9bbb59', '#f79440', '#5286bc', '#bc5051'];
  let offset = 76;
  return <g>
    <rect x="8" y={y} width="61" height="19" rx="3" fill={label === 'Path1' ? '#238725' : '#238cee'} />
    <text x="17" y={y + 13} fill="white" fontSize="10" fontWeight="700">{label}</text>
    {widths.map((width, index) => {
      const x = offset;
      offset += width;
      return <rect key={index} x={x} y={y} width={width} height="19" fill={palette[index % palette.length]} stroke="white" strokeWidth="0.5" />;
    })}
  </g>;
}

function SimulationScreen() {
  return <Panel x={8} y={8} width={544} height={364} title="SIMUL  /  XD26III : mm" orange>
    <g transform="translate(34 35)">
      {/* Exploded tooling arrangement, main spindle and cyan stock axis. */}
      <path d="M174 237 449 71" stroke="#00bec8" strokeWidth="5" fill="none" />
      <path d="m325 91 52-33 65 34v77l-52 34-65-35Z" fill="#707070" stroke="#4c4c4c" strokeWidth="2" />
      <path d="m325 91 65 36 52-35M390 127V203" stroke="#4c4c4c" strokeWidth="2" fill="none" />
      <path d="m311 128 24-15 35 20v46l-25 16-34-21Z" fill="#464646" />
      <ellipse cx="321" cy="156" rx="20" ry="28" transform="rotate(-28 321 156)" fill="#a7a7a7" stroke="#555" strokeWidth="4" />
      <path d="m293 161 22-12 12 19-22 12Z" fill="#666" />
      <path d="m64 59 22-13 105 62v100l-22 13L64 160Z" fill="#505050" />
      <path d="m64 59 105 62v100L64 160Z" fill="#797979" />
      <path d="m82 100 26-16 60 35-26 17Z" fill="#a9a9a9" />
      {[0, 1, 2, 3, 4, 5].map(index => <g key={index} transform={`translate(${80 + index * 15} ${113 + index * 9})`}>
        <path d="M0 0 12 7V60L0 53Z" fill={index % 2 ? '#666' : '#999'} stroke="#555" />
        <path d="M3 50 9 53V64L3 61Z" fill="#c7d0d4" /><circle cx="7" cy="59" r="2" fill="#00bec8" />
      </g>)}
      <ellipse cx="219" cy="214" rx="18" ry="28" transform="rotate(-28 219 214)" fill="#baaaaa" stroke="#786d6d" strokeWidth="3" />
      <ellipse cx="208" cy="221" rx="12" ry="20" transform="rotate(-28 208 221)" fill="#ded3d0" stroke="#786d6d" strokeWidth="2" />
      <path d="m71 235 28-17 30 18v28l-28 17-30-18Z" fill="#9e9e9e" stroke="#707070" />
      <path d="m260 268 22-14 32 18v27l-23 13-31-17Z" fill="#919191" stroke="#606060" />
      <path d="m266 270 34 20M271 280l-9 7m21-1-9 7m21-1-9 7" stroke="#d7dce1" strokeWidth="5" fill="none" />
      <circle cx="215" cy="216" r="39" stroke={colors.orange} strokeWidth="1.5" strokeDasharray="4 4" fill="none" />
      <path d="M244 241 283 286H357" stroke={colors.orange} strokeWidth="1.5" fill="none" />
    </g>
    {/* Orientation cube and axis triad echo the supplied simulator. */}
    <path d="m478 47 17-10 18 10v22l-18 10-17-10Z" fill="#c0c0c0" stroke="#737373" />
    <path d="m478 47 17 10 18-10M495 57V79" stroke="#737373" fill="none" />
    <path d="M522 90V49M522 90H488" stroke="#ec5656" strokeWidth="2" fill="none" />
    <text x="525" y="53" fontSize="9" fill="#ec5656">X</text><text x="482" y="100" fontSize="9" fill="#4051cc">Z</text>
    <rect x="393" y="235" width="138" height="89" rx="5" fill="#fafafa" stroke={colors.line} />
    <text x="404" y="252" fontSize="10" fontWeight="700">Machining result</text>
    <path d="M409 282H430V272H452V281H475V274H506V300H475V293H452V302H430V292H409Z" fill="#d0d4d8" stroke="#858c93" strokeWidth="1.5" />
    <path d="M408 288H514" stroke="#00bec8" strokeDasharray="3 3" fill="none" />
    <path d="M0 334H544" stroke={colors.line} fill="none" />
    <rect x="8" y="341" width="44" height="16" rx="3" fill={colors.orange} /><text x="17" y="352" fontSize="9" fill="white">Main</text>
    <rect x="57" y="341" width="42" height="16" rx="3" fill={colors.orange} /><text x="68" y="352" fontSize="9" fill="white">Sub</text>
    <text x="111" y="352" fontSize="9">Stop At</text><text x="157" y="352" fontSize="9">CHUCK</text>
    <path d="m293 342 10 7-10 7Z" fill="#454545" /><path d="M323 349H518" stroke="#a8a8a8" strokeWidth="3" fill="none" /><path d="M379 342V356" stroke={colors.orange} strokeWidth="4" fill="none" />
  </Panel>;
}

function ManagementScreen() {
  return <>
    <Panel x={8} y={8} width={544} height={182} title="Program Transfer">
      <rect x="349" y="34" width="85" height="21" rx="5" fill="#fff1e6" stroke="#f8ba8e" /><text x="361" y="48" fontSize="10" fill="#b94b08">Download</text>
      <rect x="442" y="34" width="91" height="21" rx="5" fill="#fff1e6" stroke="#f8ba8e" /><text x="461" y="48" fontSize="10" fill="#b94b08">Upload</text>
      <rect x="9" y="63" width="103" height="109" rx="4" fill="#f7f7f7" stroke={colors.line} />
      <rect x="121" y="63" width="180" height="109" rx="4" fill="#f7f7f7" stroke={colors.line} />
      <rect x="333" y="63" width="202" height="109" rx="4" fill="#f7f7f7" stroke={colors.line} />
      <text x="17" y="80" fontSize="10" fontWeight="700">Machine list</text>
      <text x="130" y="80" fontSize="10" fontWeight="700">Machine Program</text>
      <text x="342" y="80" fontSize="10" fontWeight="700">PG-Solution Program</text>
      <rect x="10" y="89" width="101" height="19" fill="#ffd7bb" />
      {['01', '02', '03', '04'].map((machine, index) => <text key={machine} x="20" y={103 + index * 19} fontSize="10" fill="#429450">{machine}</text>)}
      <text x="131" y="104" fontSize="10" fill="#b94b08">Main</text>
      <text x="141" y="124" fontSize="10">1420 (MAIN)</text><text x="141" y="143" fontSize="10">0001 (V-GROOVE)</text>
      <text x="343" y="104" fontSize="10" fill="#b94b08">Main / Sub</text>
      <text x="354" y="124" fontSize="10">1420 (MAIN)</text><text x="354" y="143" fontSize="10">1421 (SUB)</text>
      <path d="M305 113H329m-5-4 5 4-5 4M329 132H305m5-4-5 4 5 4" stroke={colors.orange} strokeWidth="2" fill="none" />
    </Panel>
    <path d="M280 194V209m-5-5 5 5 5-5" stroke={colors.orange} strokeWidth="2" fill="none" />
    <Panel x={8} y={216} width={544} height={156} title="Search / Program library">
      <rect x="8" y="34" width="55" height="113" rx="3" fill={colors.orange} />
      <text x="14" y="54" fontSize="10" fontWeight="700">Program</text>
      <rect x="8" y="65" width="55" height="25" fill="#292929" /><text x="21" y="81" fontSize="10" fill="white">APR</text>
      <rect x="75" y="35" width="288" height="21" rx="10" fill="#f0f0f0" stroke={colors.line} />
      <text x="86" y="49" fontSize="10" fill="#767676">Search programs and machining examples</text>
      <text x="80" y="72" fontSize="9">Path1</text><text x="153" y="72" fontSize="9">Process</text><text x="251" y="72" fontSize="9">Stock</text>
      <rect x="74" y="80" width="291" height="17" fill="#818181" />
      <text x="83" y="92" fontSize="9" fill="white">Stock</text><text x="174" y="92" fontSize="9" fill="white">Process</text><text x="281" y="92" fontSize="9" fill="white">Spindle</text>
      {['TURNING', 'ENDMILL', 'DRILL'].map((process, index) => <g key={process}>
        <rect x="74" y={98 + index * 16} width="291" height="16" fill={index === 0 ? '#ffdbc0' : '#f6f6f6'} stroke="#e5e5e5" />
        <text x="83" y={110 + index * 16} fontSize="9">SUS316L</text><text x="174" y={110 + index * 16} fontSize="9">{process}</text><text x="281" y={110 + index * 16} fontSize="9">3000</text>
      </g>)}
      <rect x="376" y="34" width="159" height="113" rx="4" fill="#f7f7f7" stroke={colors.line} />
      <text x="386" y="51" fontWeight="700" fontSize="10">Detail View</text>
      {['(TURNING)', 'G0 X18.0 Z20.0', 'G1 X17.0 F0.04', 'G0 Z-10.33', 'M30'].map((line, index) => <text key={line} x="386" y={71 + index * 14} fontFamily="monospace" fontSize="9" fill={index === 0 ? '#479449' : '#30599c'}>{line}</text>)}
    </Panel>
  </>;
}
